<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import SchedulerAPI from '../schedulerApi';
import SchedulerDialog from '../SchedulerDialog.vue';
import { recurrenceSummary } from '../recurrenceHelpers';
import { formatTime } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t, locale } = useI18n();
const router = useRouter();

const tab = ref('upcoming'); // upcoming | history | calendar
const rows = ref([]);
const total = ref(0);
const page = ref(1);
const perPage = ref(50);
const status = ref('');
const isLoading = ref(false);
const dialogRef = ref(null);
const calendarMonth = ref(
  new Date(new Date().getFullYear(), new Date().getMonth(), 1)
);

const STATUS_STYLE = {
  pending: 'text-n-blue-11',
  paused: 'text-n-amber-11',
  processing: 'text-n-amber-11',
  sent: 'text-n-teal-11',
  delivered: 'text-n-teal-11',
  read: 'text-n-teal-11',
  failed: 'text-n-ruby-11',
  canceled: 'text-n-slate-10',
};
const HISTORY_STATUSES = ['sent', 'delivered', 'read', 'failed', 'canceled'];

const load = async () => {
  isLoading.value = true;
  try {
    const params = { page: page.value };
    if (tab.value === 'upcoming') params.upcoming = true;
    if (tab.value === 'history' && status.value) params.status = status.value;
    if (tab.value === 'history' && !status.value)
      params.status = HISTORY_STATUSES;
    if (tab.value === 'calendar') {
      const start = calendarMonth.value;
      params.since = new Date(
        start.getFullYear(),
        start.getMonth(),
        1
      ).toISOString();
      params.until = new Date(
        start.getFullYear(),
        start.getMonth() + 1,
        0,
        23,
        59,
        59
      ).toISOString();
    }
    const { data } = await SchedulerAPI.list(params);
    rows.value = data.payload;
    total.value = data.total;
    perPage.value = data.per_page;
  } finally {
    isLoading.value = false;
  }
};
onMounted(load);

const switchTab = value => {
  tab.value = value;
  page.value = 1;
  load();
};

const statusOptions = computed(() => [
  { value: '', label: t('WASAPI_CAMPAIGNS.ALL') },
  ...HISTORY_STATUSES.map(s => ({
    value: s,
    label: t(`WASAPI_SCHEDULED.STATUS.${s.toUpperCase()}`),
  })),
]);

// group upcoming rows by day
const grouped = computed(() => {
  const map = new Map();
  rows.value.forEach(row => {
    const day = new Date(row.scheduled_at).toLocaleDateString(
      (locale.value || 'en').replace('_', '-'),
      { weekday: 'long', day: '2-digit', month: '2-digit' }
    );
    if (!map.has(day)) map.set(day, []);
    map.get(day).push(row);
  });
  return [...map.entries()];
});

// simple month calendar
const calendarDays = computed(() => {
  const start = calendarMonth.value;
  const firstDay = new Date(start.getFullYear(), start.getMonth(), 1);
  const lastDay = new Date(start.getFullYear(), start.getMonth() + 1, 0);
  const days = [];
  for (let i = 0; i < firstDay.getDay(); i += 1) days.push(null);
  for (let d = 1; d <= lastDay.getDate(); d += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), d);
    const items = rows.value.filter(
      row => new Date(row.scheduled_at).toDateString() === date.toDateString()
    );
    days.push({ date, day: d, items });
  }
  return days;
});
const monthLabel = computed(() =>
  calendarMonth.value.toLocaleDateString(
    (locale.value || 'en').replace('_', '-'),
    {
      month: 'long',
      year: 'numeric',
    }
  )
);
const shiftMonth = delta => {
  calendarMonth.value = new Date(
    calendarMonth.value.getFullYear(),
    calendarMonth.value.getMonth() + delta,
    1
  );
  load();
};

const action = async (row, name) => {
  try {
    if (name === 'cancel') await SchedulerAPI.cancel(row.id);
    else await SchedulerAPI.action(row.id, name);
    useAlert(t(`WASAPI_SCHEDULER.ACTIONS.${name.toUpperCase()}_DONE`));
    load();
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_SCHEDULED.SAVE_ERROR'));
  }
};

const label = row =>
  row.name ||
  (row.template_params
    ? t('WASAPI_SCHEDULED.TEMPLATE_LABEL', { name: row.template_params.name })
    : row.content);

const openConversation = row => {
  if (!row.conversation_id) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: row.conversation_id },
  });
};

const totalPages = computed(() =>
  Math.max(1, Math.ceil(total.value / perPage.value))
);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="w-full max-w-5xl px-6 py-6 mx-auto flex flex-col gap-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex flex-col gap-1">
          <h1 class="text-xl font-medium text-n-slate-12">
            {{ t('WASAPI_SCHEDULER.TITLE') }}
          </h1>
          <p class="text-sm text-n-slate-11">
            {{ t('WASAPI_SCHEDULER.DESCRIPTION') }}
          </p>
        </div>
        <Button
          :label="t('WASAPI_SCHEDULED.NEW')"
          icon="i-lucide-plus"
          color="blue"
          size="sm"
          @click="dialogRef.open()"
        />
      </header>

      <div class="flex items-center gap-2">
        <Button
          v-for="value in ['upcoming', 'history', 'calendar']"
          :key="value"
          :label="t(`WASAPI_SCHEDULER.TABS.${value.toUpperCase()}`)"
          :variant="tab === value ? 'solid' : 'faded'"
          color="slate"
          size="sm"
          @click="switchTab(value)"
        />
        <span class="flex-1" />
        <div class="w-48 shrink-0">
          <ComboBox
            v-if="tab === 'history'"
            v-model="status"
            :options="statusOptions"
            @update:model-value="load"
          />
        </div>
      </div>

      <div v-if="isLoading && !rows.length" class="flex justify-center py-10">
        <Spinner />
      </div>

      <template v-else-if="tab === 'calendar'">
        <div class="flex items-center justify-between">
          <Button
            icon="i-lucide-chevron-left"
            variant="ghost"
            color="slate"
            size="sm"
            @click="shiftMonth(-1)"
          />
          <span class="text-sm font-medium text-n-slate-12 capitalize">{{
            monthLabel
          }}</span>
          <Button
            icon="i-lucide-chevron-right"
            variant="ghost"
            color="slate"
            size="sm"
            @click="shiftMonth(1)"
          />
        </div>
        <div class="grid grid-cols-7 gap-1">
          <span
            v-for="day in [0, 1, 2, 3, 4, 5, 6]"
            :key="`h-${day}`"
            class="py-1 text-xs text-center text-n-slate-10"
          >
            {{ t(`WASAPI_SCHEDULER.RECURRENCE.WEEKDAYS.${day}`) }}
          </span>
          <div
            v-for="(cell, index) in calendarDays"
            :key="index"
            class="min-h-20 p-1 rounded-lg text-xs"
            :class="cell ? 'outline outline-1 outline-n-weak bg-n-solid-1' : ''"
          >
            <template v-if="cell">
              <span class="text-n-slate-10">{{ cell.day }}</span>
              <button
                v-for="item in cell.items.slice(0, 3)"
                :key="item.id"
                type="button"
                class="block w-full px-1 py-0.5 mt-0.5 truncate text-left rounded bg-n-brand/10 text-n-slate-12 hover:bg-n-brand/20"
                :title="label(item)"
                @click="dialogRef.open(item)"
              >
                {{ formatTime(item.scheduled_at, locale) }}
                {{ label(item) }}
              </button>
              <span v-if="cell.items.length > 3" class="text-n-slate-10">
                {{ `+${cell.items.length - 3}` }}
              </span>
            </template>
          </div>
        </div>
      </template>

      <template v-else>
        <p v-if="!rows.length" class="text-sm text-n-slate-11">
          {{ t('WASAPI_SCHEDULER.EMPTY') }}
        </p>
        <div
          v-for="[day, items] in grouped"
          :key="day"
          class="flex flex-col gap-2"
        >
          <h2 class="text-xs font-medium uppercase text-n-slate-10">
            {{ day }}
          </h2>
          <div
            v-for="row in items"
            :key="row.id"
            class="flex items-center gap-3 p-3 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <span
              class="text-sm font-semibold tabular-nums text-n-slate-12 w-12"
            >
              {{ formatTime(row.scheduled_at, locale) }}
            </span>
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm text-n-slate-12 truncate">{{
                label(row)
              }}</span>
              <span
                class="flex flex-wrap items-center gap-x-2 text-xs text-n-slate-11"
              >
                <button
                  v-if="row.contact"
                  type="button"
                  class="hover:underline"
                  :disabled="!row.conversation_id"
                  @click="openConversation(row)"
                >
                  {{ row.contact.name }}
                </button>
                <span>· {{ row.inbox.name }}</span>
                <span v-if="row.recurrence"
                  >· <Icon icon="i-lucide-repeat" class="inline size-3" />
                  {{ recurrenceSummary(row.recurrence, t) }}</span
                >
                <span v-if="row.cancel_on_reply"
                  >· {{ t('WASAPI_SCHEDULER.CANCEL_ON_REPLY_BADGE') }}</span
                >
                <span v-if="row.attachments.length"
                  >· <Icon icon="i-lucide-paperclip" class="inline size-3" />
                  {{ row.attachments.length }}</span
                >
                <span v-if="row.error" class="text-n-ruby-11">
                  {{ `· ${row.error}` }}
                </span>
              </span>
            </div>
            <span
              class="text-xs font-medium shrink-0"
              :class="STATUS_STYLE[row.status]"
            >
              {{ t(`WASAPI_SCHEDULED.STATUS.${row.status.toUpperCase()}`) }}
            </span>
            <div class="flex items-center shrink-0">
              <template v-if="['pending', 'paused'].includes(row.status)">
                <Button
                  v-tooltip="t('WASAPI_SCHEDULED.EDIT')"
                  icon="i-lucide-pen"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="dialogRef.open(row)"
                />
                <Button
                  v-if="row.status === 'pending'"
                  v-tooltip="t('WASAPI_SCHEDULER.ACTIONS.PAUSE')"
                  icon="i-lucide-pause"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="action(row, 'pause')"
                />
                <Button
                  v-else
                  v-tooltip="t('WASAPI_SCHEDULER.ACTIONS.RESUME')"
                  icon="i-lucide-play"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="action(row, 'resume')"
                />
                <Button
                  v-tooltip="t('WASAPI_SCHEDULER.ACTIONS.SEND_NOW')"
                  icon="i-lucide-send"
                  variant="ghost"
                  color="blue"
                  size="sm"
                  @click="action(row, 'send_now')"
                />
                <Button
                  v-tooltip="t('WASAPI_SCHEDULER.ACTIONS.CANCEL')"
                  icon="i-lucide-x"
                  variant="ghost"
                  color="ruby"
                  size="sm"
                  @click="action(row, 'cancel')"
                />
              </template>
              <Button
                v-tooltip="t('WASAPI_SCHEDULER.ACTIONS.DUPLICATE')"
                icon="i-lucide-copy"
                variant="ghost"
                color="slate"
                size="sm"
                @click="action(row, 'duplicate')"
              />
            </div>
          </div>
        </div>
        <div
          v-if="totalPages > 1"
          class="flex items-center justify-end gap-2 text-sm"
        >
          <Button
            icon="i-lucide-chevron-left"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="page <= 1"
            @click="
              page -= 1;
              load();
            "
          />
          <span class="text-n-slate-11">{{ page }} / {{ totalPages }}</span>
          <Button
            icon="i-lucide-chevron-right"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="page >= totalPages"
            @click="
              page += 1;
              load();
            "
          />
        </div>
      </template>
    </div>
    <SchedulerDialog ref="dialogRef" @saved="load" />
  </section>
</template>
